import { useEffect } from 'react';
import { useWardStore } from '@/stores/wardStore';
import { useComparisonStore } from '@/stores/comparisonStore';

const STORAGE_KEYS = {
  SELECTED_WARD_ID: 'bansaosohanoi_selected_ward',
  COMPARISON_WARD_IDS: 'bansaosohanoi_comparison_wards',
  IS_COMPARING: 'bansaosohanoi_is_comparing',
} as const;

/**
 * Hook to persist and restore ward selections from localStorage
 */
export function useSelectionPersistence() {
  const { wards, selectedWard, selectWard } = useWardStore();
  const { selectedWards, isComparing, addWard, toggleComparing } = useComparisonStore();

  // Save selections to localStorage
  useEffect(() => {
    if (selectedWard) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_WARD_ID, selectedWard.objectid.toString());
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_WARD_ID);
    }
  }, [selectedWard]);

  useEffect(() => {
    if (selectedWards.length > 0) {
      const ids = selectedWards.map(w => w.objectid);
      localStorage.setItem(STORAGE_KEYS.COMPARISON_WARD_IDS, JSON.stringify(ids));
    } else {
      localStorage.removeItem(STORAGE_KEYS.COMPARISON_WARD_IDS);
    }
  }, [selectedWards]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.IS_COMPARING, isComparing.toString());
  }, [isComparing]);

  // Restore selections when wards are loaded
  useEffect(() => {
    if (wards.length === 0) return;

    // Restore selected ward
    const savedWardId = localStorage.getItem(STORAGE_KEYS.SELECTED_WARD_ID);
    if (savedWardId && !selectedWard) {
      const ward = wards.find(w => w.objectid === parseInt(savedWardId));
      if (ward) {
        selectWard(ward);
      } else {
        // Ward no longer exists, clear from storage
        localStorage.removeItem(STORAGE_KEYS.SELECTED_WARD_ID);
      }
    }

    // Restore comparison selections
    const savedComparisonIds = localStorage.getItem(STORAGE_KEYS.COMPARISON_WARD_IDS);
    if (savedComparisonIds) {
      try {
        const ids: number[] = JSON.parse(savedComparisonIds);
        ids.forEach(id => {
          const ward = wards.find(w => w.objectid === id);
          if (ward && !selectedWards.some(w => w.objectid === id)) {
            addWard(ward);
          }
        });
      } catch (error) {
        console.error('Failed to restore comparison selections:', error);
        localStorage.removeItem(STORAGE_KEYS.COMPARISON_WARD_IDS);
      }
    }

    // Restore comparison mode
    const savedIsComparing = localStorage.getItem(STORAGE_KEYS.IS_COMPARING);
    if (savedIsComparing === 'true' && !isComparing) {
      toggleComparing();
    }
  }, [wards]); // Only run when wards are loaded

  // Clear all persisted selections
  const clearPersistedSelections = () => {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_WARD_ID);
    localStorage.removeItem(STORAGE_KEYS.COMPARISON_WARD_IDS);
    localStorage.removeItem(STORAGE_KEYS.IS_COMPARING);
  };

  return { clearPersistedSelections };
}